import {
  Badge,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { mdiFilter, mdiSort } from '@mdi/js';
import Icon from '@mdi/react';
import Button from 'components/Button/Button';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import Loading from 'components/Loading/Loading';
import { ProjectPageRedesignProvider } from 'components/ProjectPageRedesign/ProjectPageRedesignContext';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  formatActivityDaySubtitle,
  getActivityActionLabel,
} from 'scenes/Project/components/ProjectOverview/activityFeedFormat';
import {
  selectActivityLogError,
  selectActivityLogImages,
  selectActivityLogItems,
  selectActivityLogLoading,
} from 'store/activityLog/activityLogSlice';
import { fetchActivityLogsForUserProjectDate } from 'store/activityLog/thunks/fetchActivityLogsForUserProjectDate.thunk';
import { selectIsProjectPageRedesignEnabled } from 'store/user/selectors.js/user.selectors';
import ActivityLogDialogRedesign from './ActivityLogDialogRedesign';
import ActivityLogRow, { IMAGE_TARGET_TYPE } from './ActivityLogRow';
import {
  ActivityImagePreview,
  useActivityImagePreview,
} from './activityImagePreview';

export { toPreviewFilePermission } from './activityImagePreview';

const DEFAULT_SORT = 'TIME_ASC';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    ...theme.component.dialogPaper,
    // Always use the full available height, even with few rows.
    height: 'calc(100% - 64px)',
    [theme.breakpoints.down('xs')]: {
      ...theme.component.dialogPaperFullscreen,
    },
  },
  dialogActions: {
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
  },
  // Top bar: activity count on the left, filter + sort buttons on the right.
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(0.5),
    paddingBottom: theme.spacing(1),
  },
  toolbarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
}));

/**
 * Lists a user's activity logs on a project for a single day — the viewer's
 * local day, midnight to midnight (see fetchActivityLogsForUserProjectDate),
 * without the rows of opening the project. Each row shows two icons (the
 * verb, e.g. eye for "viewed", and the feature, e.g. calendar for the
 * timestamps page), the time and what the user did, worded as the dashboard
 * feed words it. Rows about an image show it small; clicking it opens the
 * gallery's image preview, view-only, paging through the day's images. A top
 * bar shows the activity count and offers sort + filter (by action) menus.
 * Visibility is controlled by the activityLog slice.
 *
 * It is mounted once for the whole app (RecordDialogContainer, in Layout),
 * outside any view's redesign provider, so it reads the user's own choice
 * (UserSetting projectPageRedesign, selectIsProjectPageRedesignEnabled): on,
 * the redesigned dialog (ActivityLogDialogRedesign); off, the classic one as
 * it was.
 */
const ActivityLogDialog = ({ open, onClose, userId, projectId, date }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation('common');

  const items = useSelector(selectActivityLogItems);
  const images = useSelector(selectActivityLogImages);
  const loading = useSelector(selectActivityLogLoading);
  const error = useSelector(selectActivityLogError);
  const redesign = useSelector(selectIsProjectPageRedesignEnabled);
  // The user as the store knows them, to name them before their rows do.
  const knownUser = useSelector(
    (state) => state.projectUsers?.users?.entities?.[userId]
  );

  const [sortBy, setSortBy] = useState(DEFAULT_SORT);
  const [actionFilters, setActionFilters] = useState([]); // empty = all actions
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [sortAnchor, setSortAnchor] = useState(null);

  const load = useCallback(() => {
    if (projectId && userId && date) {
      dispatch(
        fetchActivityLogsForUserProjectDate({ projectId, userId, date })
      );
    }
  }, [dispatch, projectId, userId, date]);

  const sortOptions = [
    { value: 'TIME_ASC', label: t('activity_log.sort_oldest', 'Vanhin ensin') },
    {
      value: 'TIME_DESC',
      label: t('activity_log.sort_newest', 'Uusin ensin'),
    },
    {
      value: 'ACTION_ASC',
      label: t('activity_log.sort_action_asc', 'Toiminto A–Ö'),
    },
    {
      value: 'ACTION_DESC',
      label: t('activity_log.sort_action_desc', 'Toiminto Ö–A'),
    },
  ];

  const actionLabel = useCallback(
    (action) => getActivityActionLabel(t, action),
    [t]
  );

  const distinctActions = useMemo(
    () =>
      [...new Set((items || []).map((item) => item.action))].sort((a, b) =>
        actionLabel(a).localeCompare(actionLabel(b))
      ),
    [items, actionLabel]
  );

  // Aggregate (employer-project) mode returns logs from multiple projects; show the
  // project name on each row so they're distinguishable. A normal single-project
  // dialog leaves it off (redundant there).
  const showProject = useMemo(
    () => new Set((items || []).map((item) => item.projectId)).size > 1,
    [items]
  );

  const filteredItems = useMemo(() => {
    if (actionFilters.length === 0) {
      return items || [];
    }
    return (items || []).filter((item) => actionFilters.includes(item.action));
  }, [items, actionFilters]);

  const sortedItems = useMemo(() => {
    const next = [...filteredItems];
    next.sort((a, b) => {
      switch (sortBy) {
        case 'TIME_DESC':
          return (b.createdAt || '').localeCompare(a.createdAt || '');
        case 'ACTION_ASC':
          return actionLabel(a.action).localeCompare(actionLabel(b.action));
        case 'ACTION_DESC':
          return actionLabel(b.action).localeCompare(actionLabel(a.action));
        case 'TIME_ASC':
        default:
          return (a.createdAt || '').localeCompare(b.createdAt || '');
      }
    });
    return next;
  }, [filteredItems, sortBy, actionLabel]);

  // The images the preview pages through: those the viewer may see, once
  // each, in the order the list shows them.
  const preview = useActivityImagePreview(sortedItems, images);
  const { closePreview, openImage: handleOpenImage } = preview;

  useEffect(() => {
    if (open) {
      setActionFilters([]);
      setSortBy(DEFAULT_SORT);
      closePreview();
      load();
    }
  }, [open, load, closePreview]);

  const toggleActionFilter = (action) => {
    setActionFilters((prev) =>
      prev.includes(action)
        ? prev.filter((value) => value !== action)
        : [...prev, action]
    );
  };

  const title = date
    ? t('activity_log.day_title', {
        date: moment(date, 'YYYY-MM-DD').format('l'),
        defaultValue: 'Aktiviteettiloki – {{date}}',
      })
    : t('activity_log.title', 'Aktiviteettiloki');

  const menus = (
    <>
      <Menu
        anchorEl={filterAnchor}
        open={Boolean(filterAnchor)}
        onClose={() => setFilterAnchor(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {distinctActions.map((action) => {
          const checked = actionFilters.includes(action);
          return (
            <MenuItem
              key={action}
              onClick={() => toggleActionFilter(action)}
              dense
            >
              <ListItemIcon style={{ minWidth: 36 }}>
                <Checkbox
                  edge="start"
                  checked={checked}
                  tabIndex={-1}
                  disableRipple
                  size="small"
                />
              </ListItemIcon>
              <ListItemText primary={actionLabel(action)} />
            </MenuItem>
          );
        })}
        {actionFilters.length > 0 && [
          <Divider key="divider" />,
          <MenuItem key="clear" onClick={() => setActionFilters([])} dense>
            <ListItemText
              primary={t('activity_log.clear_filters', 'Tyhjennä suodattimet')}
            />
          </MenuItem>,
        ]}
      </Menu>
      <Menu
        anchorEl={sortAnchor}
        open={Boolean(sortAnchor)}
        onClose={() => setSortAnchor(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {sortOptions.map((option) => (
          <MenuItem
            key={option.value}
            selected={sortBy === option.value}
            onClick={() => {
              setSortBy(option.value);
              setSortAnchor(null);
            }}
            dense
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );

  const rows = sortedItems.map((item) => (
    <ActivityLogRow
      key={item.sk || item.id || `${item.createdAt}-${item.action}`}
      item={item}
      showProject={showProject}
      image={
        item.targetType === IMAGE_TARGET_TYPE && item.targetId
          ? images?.[item.targetId]
          : undefined
      }
      onOpenImage={handleOpenImage}
    />
  ));

  const imagePreview = <ActivityImagePreview open={open} preview={preview} />;

  if (redesign) {
    // As the project's day dialog: whose day it is over the weekday, the
    // date and the events shown (once they are in).
    const userName =
      (items || []).find((item) => item.userName)?.userName ||
      [knownUser?.firstName, knownUser?.lastName].filter(Boolean).join(' ');
    const loaded = !loading && !error && Boolean(items && items.length > 0);
    return (
      <>
        <ActivityLogDialogRedesign
          open={open}
          onClose={onClose}
          title={userName || t('activity_log.title', 'Aktiviteettiloki')}
          subtitle={
            date
              ? formatActivityDaySubtitle(
                  t,
                  date,
                  loaded ? sortedItems.length : null
                )
              : null
          }
          loading={loading}
          error={error}
          onRetry={load}
          hasItems={Boolean(items && items.length > 0)}
          count={sortedItems.length}
          filterCount={actionFilters.length}
          onOpenFilter={(event) => setFilterAnchor(event.currentTarget)}
          onOpenSort={(event) => setSortAnchor(event.currentTarget)}
          menus={menus}
        >
          {rows}
        </ActivityLogDialogRedesign>
        {imagePreview}
      </>
    );
  }

  return (
    // The classic dialog as it was, whatever look the view under it has.
    <ProjectPageRedesignProvider value={false}>
      <Dialog
        open={open}
        classes={{ paper: classes.dialogPaper }}
        onClose={onClose}
        aria-labelledby="activity-log-dialog"
        maxWidth="sm"
        fullWidth
      >
        <DialogHeader onClose={onClose} title={title} />
        <DialogContent>
          {loading ? (
            <Loading tiny />
          ) : error ? (
            <div role="alert">
              <Typography variant="body2" color="error" paragraph>
                {t(
                  'activity_log.load_error',
                  'Aktiviteettilokin lataus epäonnistui'
                )}
              </Typography>
              <Button onClick={load} color="dark">
                {t('common.retry', 'Yritä uudelleen')}
              </Button>
            </div>
          ) : (
            <>
              {items && items.length > 0 ? (
                <div className={classes.toolbar}>
                  <Typography variant="body2" color="textSecondary">
                    {t('activity_log.activity_count', {
                      count: sortedItems.length,
                      defaultValue: 'Tapahtumia: {{count}}',
                    })}
                  </Typography>
                  <div className={classes.toolbarActions}>
                    <Tooltip title={t('activity_log.filter', 'Suodata')}>
                      <IconButton
                        size="small"
                        onClick={(event) =>
                          setFilterAnchor(event.currentTarget)
                        }
                        aria-label={t('activity_log.filter', 'Suodata')}
                      >
                        <Badge
                          color="secondary"
                          badgeContent={actionFilters.length}
                          invisible={actionFilters.length === 0}
                        >
                          <Icon path={mdiFilter} size={1} />
                        </Badge>
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('activity_log.sort', 'Järjestä')}>
                      <IconButton
                        size="small"
                        onClick={(event) => setSortAnchor(event.currentTarget)}
                        aria-label={t('activity_log.sort', 'Järjestä')}
                      >
                        <Icon path={mdiSort} size={1} />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              ) : null}

              {menus}

              {sortedItems.length > 0 ? (
                rows
              ) : (
                <Typography variant="body2" color="textSecondary">
                  {t('activity_log.no_logs', 'Ei tapahtumia tältä päivältä')}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <Divider />
        <DialogActions className={classes.dialogActions}>
          <Button onClick={onClose} color="dark">
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
      {imagePreview}
    </ProjectPageRedesignProvider>
  );
};

export default ActivityLogDialog;
